import { useMutation } from "@tanstack/react-query";

import { apiPaths } from "@/config/paths";
import type { MutationConfig } from "@/lib/react-query";
import { authApi } from "@/lib/api-client";
import { clearStoredPromotionCode } from "@blueskydataplatform/shared-ui/store/use-order";
import { pushDataLayerEvent } from "@blueskydataplatform/shared-ui/lib/gtm";
import { AxiosResponse } from "axios";
import { isAccountSignUp, type SignUpResponse } from "./is-account-sign-up";

export type SignUpInput = {
  email: string;
  /** Salutation; merged into Stripe customer metadata with sign-up extensions. */
  title?: string;
  firstName?: string;
  lastName?: string;
  /** Account display name — first + last name on platform sign-up; legacy forms may send a single full name. Omitted by the lean MCP form. */
  fullName?: string;
  /** Omitted only by the lean MCP form, where the server generates one. */
  password?: string;
  companyName?: string;
  numEmployees?: string;
  useCase?: string;
  useCaseId?: number | null;
  orgType?: string;
  /** Optional invoice address for the new org; omitted means invoices go to `email`. */
  billingEmail?: string;
  userType?: string;
  hasCheckedVal?: boolean;
  inviteCode?: string | null;
  referrerCode?: string | null;
  partner?: string | null;
  isFromMCP?: boolean;
  provider?: string;
  /** MCP OAuth state, echoed so the server can identify the calling LLM from shared Redis. */
  state?: string | null;
  signUpExtensions?: Record<string, string | boolean>;
  subscribeToMarketingEmails?: boolean;
};

const signUp = ({ data }: { data: SignUpInput }): Promise<AxiosResponse<SignUpResponse>> => {
  return authApi.post<SignUpInput, AxiosResponse<SignUpResponse>>(apiPaths.auth.signUp.getPath(), data);
};

type UseSignUpOptions = {
  mutationConfig?: MutationConfig<typeof signUp>;
};

export const useSignUp = ({ mutationConfig }: UseSignUpOptions = {}) => {
  const { onSuccess, ...restConfig } = mutationConfig || {};

  return useMutation({
    onSuccess: (data, ...args) => {
      clearStoredPromotionCode();
      // Every sign-up form routes through here, so the conversion signal is
      // emitted once. The tags that listen for it (LinkedIn, Reddit, Google Ads)
      // are configured per-env in the GTM container, not in this repo.
      if (isAccountSignUp(data.data)) {
        pushDataLayerEvent("sign_up");
      }
      onSuccess?.(data, ...args);
    },
    ...restConfig,
    mutationFn: signUp,
  });
};
